import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FolderLock, Network, Activity, Clock, TrendingUp, Coins, Target, Wallet, CalendarRange, AlertCircle } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { cockpit } from "@/lib/cockpitAuth";

const STATUS_LABELS = {
    draft: "Brouillon",
    audit: "Audit",
    analyse: "Analyse",
    structuration: "Structuration",
    qualification: "Qualification",
    discussion: "Discussions",
    closing: "Closing",
    live: "En cours",
    paused: "Pause",
    closed: "Clos",
};

const ACTION_LABELS = {
    login: "Connexion",
    dossier_created: "Dossier créé",
    dossier_updated: "Dossier mis à jour",
    dossier_deleted: "Dossier supprimé",
    timeline_updated: "Timeline mise à jour",
    partner_created: "Partenaire ajouté",
    partner_updated: "Partenaire modifié",
    partner_deleted: "Partenaire supprimé",
    doc_uploaded: "Document déposé",
    doc_deleted: "Document supprimé",
    invoice_created: "Facture créée",
    invoice_updated: "Facture mise à jour",
    invoice_deleted: "Facture supprimée",
    totp_enabled: "TOTP activé",
    totp_disabled: "TOTP désactivé",
};

const EUR = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const formatEUR = (n) => EUR.format(Number(n) || 0);

function ageLabel(iso) {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "à l'instant";
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `il y a ${h} h`;
    const d = Math.floor(h / 24);
    return `il y a ${d} j`;
}

export default function CockpitDashboard() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    const load = () =>
        cockpit
            .get("/dashboard")
            .then(({ data }) => setData(data))
            .catch((e) => setErr(e?.response?.data?.detail || "Erreur de chargement"));

    useEffect(() => {
        load();
    }, []);

    if (err)
        return (
            <div className="p-16 text-[#e89090] text-sm" data-testid="cockpit-dashboard-error">
                {err}
            </div>
        );
    if (!data) return <div className="p-16 text-[#f4ecd8]/40 text-sm">Chargement…</div>;

    const { totals, finance, commercial, pipeline, sources, maturity, priorities, recent_dossiers, recent_activity, recent_clients } = data;

    return (
        <div data-testid="cockpit-dashboard" className="px-5 sm:px-8 lg:px-16 py-10 lg:py-14">
            {/* Header */}
            <header className="mb-10 lg:mb-14">
                <div className="overline text-[#d4af37]/70 mb-4">
                    Vue d'ensemble · Cockpit
                </div>
                <h1
                    className="font-serif text-[28px] sm:text-[36px] md:text-[44px] lg:text-[52px] leading-[1.08] text-[#f4ecd8]"
                    style={{ fontWeight: 300 }}
                >
                    Bonjour.{" "}
                    <em className="not-italic text-[#d4af37]">
                        Voici l'état des opérations.
                    </em>
                </h1>

                {/* Activité en cours + Dernière avancée — bandeau discret */}
                <LiveMomentumLine
                    commercial={commercial}
                    recent_activity={recent_activity}
                    recent_dossiers={recent_dossiers}
                />
            </header>

            {/* CLIENTS NEEDING FOLLOWUP — silent > 15 days */}
            <FollowupAlertsBlock />

            {/* TOTALS */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] mb-12 lg:mb-14">
                {[
                    { l: "Dossiers actifs", v: totals.active_dossiers, t: "Hors clos / pause", icon: Activity },
                    { l: "Dossiers totaux", v: totals.dossiers, t: "Y compris archivés", icon: FolderLock },
                    { l: "Partenaires", v: totals.partners, t: "Répertoire privé", icon: Network },
                ].map((s) => {
                    const Icon = s.icon;
                    return (
                        <div
                            key={s.l}
                            className="bg-[#0b1220] p-7 sm:p-10"
                            data-testid={`cockpit-total-${s.l}`}
                        >
                            <div className="flex items-start justify-between mb-6 sm:mb-8">
                                <span className="overline text-[#d4af37]/80">
                                    {s.l}
                                </span>
                                <Icon
                                    strokeWidth={1}
                                    size={20}
                                    className="text-[#d4af37]/60"
                                />
                            </div>
                            <div
                                className="font-serif text-[48px] sm:text-[64px] leading-none text-[#f4ecd8] mb-3"
                                style={{ fontWeight: 300 }}
                            >
                                {s.v}
                            </div>
                            <p className="text-[12px] text-[#f4ecd8]/45 tracking-wide">
                                {s.t}
                            </p>
                        </div>
                    );
                })}
            </section>

            {/* FINANCIAL OVERVIEW */}
            {finance && <FinancialOverview finance={finance} />}

            {/* FINANCIAL TIME SERIES + MONTHLY TARGET */}
            <FinanceTimeseries finance={finance} onTargetSaved={() => load()} />

            {/* PRIORITIES — strategic to-do list */}
            <PrioritiesPanel initial={priorities || []} />

            {/* COMMERCIAL — discreet 4 KPIs */}
            {commercial && (
                <CommercialPerformance
                    commercial={commercial}
                    pipeline={pipeline || []}
                />
            )}

            {/* PIPELINE + SOURCES + MATURITY — discreet rows */}
            <PipelineSourcesMaturity
                pipeline={pipeline || []}
                sources={sources || []}
                maturity={maturity || []}
            />

            {/* TWO COLUMNS */}
            <section className="grid lg:grid-cols-12 gap-10">
                {/* RECENT DOSSIERS */}
                <div className="lg:col-span-7">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <div className="overline text-[#d4af37]/70 mb-3">
                                Dossiers récents
                            </div>
                            <h2
                                className="font-serif text-[24px] text-[#f4ecd8]"
                                style={{ fontWeight: 400 }}
                            >
                                Pipeline en cours
                            </h2>
                        </div>
                        <Link
                            to="/cockpit/dossiers"
                            data-testid="cockpit-all-dossiers"
                            className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 hover:text-[#d4af37] transition-colors duration-500"
                        >
                            Tous les dossiers
                            <ArrowUpRight strokeWidth={1.2} size={12} />
                        </Link>
                    </div>
                    <div className="border-t border-white/[0.06]">
                        {recent_dossiers.length === 0 ? (
                            <p className="text-[#f4ecd8]/40 text-sm py-8">
                                Aucun dossier pour le moment.{" "}
                                <Link
                                    to="/cockpit/dossiers/new"
                                    className="text-[#d4af37] underline-offset-4 hover:underline"
                                >
                                    Créer le premier
                                </Link>
                                .
                            </p>
                        ) : (
                            recent_dossiers.map((d) => (
                                <Link
                                    key={d.id}
                                    to={`/cockpit/dossiers/${d.id}`}
                                    data-testid={`recent-dossier-${d.id}`}
                                    className="group flex items-center justify-between gap-6 py-5 border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors duration-500 px-2"
                                >
                                    <div className="min-w-0">
                                        <div
                                            className="font-serif text-[19px] text-[#f4ecd8] leading-tight truncate"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {d.name}
                                        </div>
                                        <div className="mt-1.5 text-[12px] text-[#f4ecd8]/45 tracking-wide">
                                            {d.sector || "Secteur n.c."} ·{" "}
                                            {ageLabel(d.updated_at)}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-5 shrink-0">
                                        <span className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/80">
                                            {STATUS_LABELS[d.status] || d.status}
                                        </span>
                                        <ArrowUpRight
                                            strokeWidth={1.2}
                                            size={14}
                                            className="text-[#f4ecd8]/30 group-hover:text-[#d4af37] group-hover:translate-x-0.5 transition-all duration-500"
                                        />
                                    </div>
                                </Link>
                            ))
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMN — activity + clients */}
                <div className="lg:col-span-5 space-y-12">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3">
                            Cinq derniers accès clients
                        </div>
                        <h3
                            className="font-serif text-[20px] text-[#f4ecd8] mb-8"
                            style={{ fontWeight: 400 }}
                        >
                            <Clock
                                strokeWidth={1}
                                size={16}
                                className="inline-block mr-2 text-[#d4af37]"
                            />
                            Suivi confidentiel
                        </h3>
                        {recent_clients.length === 0 ? (
                            <p className="text-[#f4ecd8]/40 text-sm italic">
                                Aucun accès client enregistré pour le moment.
                            </p>
                        ) : (
                            <ul className="space-y-0">
                                {recent_clients.map((c, i) => (
                                    <li
                                        key={c.id || i}
                                        className="border-t border-white/[0.06] py-3 flex items-center justify-between gap-4"
                                    >
                                        <span className="text-[13px] text-[#f4ecd8]/75 font-serif">
                                            {c.email}
                                        </span>
                                        <span className="text-[11px] text-[#d4af37]/70 tracking-wide">
                                            {ageLabel(c.at)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3">
                            Journal d'activité
                        </div>
                        <h3
                            className="font-serif text-[20px] text-[#f4ecd8] mb-8"
                            style={{ fontWeight: 400 }}
                        >
                            Dernières actions internes
                        </h3>
                        <ul className="space-y-0">
                            {recent_activity.slice(0, 8).map((a, i) => (
                                <li
                                    key={a.id || i}
                                    className="border-t border-white/[0.06] py-3 flex items-start justify-between gap-4"
                                >
                                    <div className="min-w-0">
                                        <div className="text-[13px] text-[#f4ecd8]/85 font-serif">
                                            {ACTION_LABELS[a.action] || a.action}
                                        </div>
                                        <div className="text-[11px] text-[#f4ecd8]/40 mt-0.5">
                                            {a.actor_email}
                                        </div>
                                    </div>
                                    <span className="text-[11px] text-[#d4af37]/70 tracking-wide shrink-0">
                                        {ageLabel(a.at)}
                                    </span>
                                </li>
                            ))}
                            {recent_activity.length === 0 && (
                                <li className="text-[#f4ecd8]/40 text-sm py-3 italic">
                                    Aucune activité enregistrée.
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </section>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────
// FinancialOverview — KPI cards + pie chart roll-up
// ──────────────────────────────────────────────────────────────

const KPI = [
    {
        key: "revenue_realized",
        label: "Honoraires encaissés",
        sub: "Factures payées",
        accent: "#d4af37",
        icon: Coins,
    },
    {
        key: "revenue_pending",
        label: "Chiffre d'affaires en cours",
        sub: "Factures émises ou à venir",
        accent: "#8fa3c4",
        icon: TrendingUp,
    },
    {
        key: "revenue_total_engaged",
        label: "Chiffre d'affaires réalisé",
        sub: "Encaissé + en cours",
        accent: "#f4ecd8",
        icon: Wallet,
    },
    {
        key: "revenue_target",
        label: "Chiffre d'affaires cible",
        sub: "Engagement projeté",
        accent: "#e8c46b",
        icon: Target,
    },
];

function FinancialOverview({ finance }) {
    const realized = Number(finance.revenue_realized) || 0;
    const pending = Number(finance.revenue_pending) || 0;
    const target = Number(finance.revenue_target) || 0;
    const remaining = Math.max(0, target - realized - pending);

    const chartData = [
        { name: "Encaissé", value: realized, color: "#d4af37" },
        { name: "En cours", value: pending, color: "#8fa3c4" },
        { name: "Reste à réaliser", value: remaining, color: "#3a4960" },
    ].filter((s) => s.value > 0);

    const total = realized + pending + remaining;
    const progressPct = target > 0
        ? Math.min(100, ((realized + pending) / target) * 100)
        : 0;

    return (
        <section
            data-testid="cockpit-finance-overview"
            className="mb-14 lg:mb-16"
        >
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3">
                        Tableau de bord financier
                    </div>
                    <h2
                        className="font-serif text-[24px] sm:text-[28px] text-[#f4ecd8]"
                        style={{ fontWeight: 400 }}
                    >
                        Synthèse honoraires &amp; chiffre d'affaires
                    </h2>
                    <SyntheseExtras
                        targetCumulative={
                            finance?.revenue_target_cumulative || 0
                        }
                        motivation={finance?.motivation_phrase || ""}
                    />
                </div>
                {target > 0 && (
                    <div className="text-right">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-1">
                            Atteinte de la cible
                        </div>
                        <div
                            className="font-serif text-[28px] text-[#f4ecd8]"
                            style={{ fontWeight: 300 }}
                        >
                            {progressPct.toFixed(1)}%
                        </div>
                    </div>
                )}
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06] mb-10">
                {KPI.map((k) => {
                    const Icon = k.icon;
                    return (
                        <div
                            key={k.key}
                            data-testid={`cockpit-kpi-${k.key}`}
                            className="bg-[#0b1220] p-6 sm:p-8"
                        >
                            <div className="flex items-start justify-between mb-5">
                                <span
                                    className="text-[10px] tracking-[0.26em] uppercase"
                                    style={{ color: `${k.accent}cc` }}
                                >
                                    {k.label}
                                </span>
                                <Icon
                                    strokeWidth={1}
                                    size={18}
                                    style={{ color: `${k.accent}88` }}
                                />
                            </div>
                            <div
                                className="font-serif text-[26px] sm:text-[32px] leading-none text-[#f4ecd8] mb-2"
                                style={{ fontWeight: 300 }}
                            >
                                {formatEUR(finance[k.key])}
                            </div>
                            <p className="text-[11px] text-[#f4ecd8]/40 tracking-wide italic">
                                {k.sub}
                            </p>
                        </div>
                    );
                })}
            </div>

            {/* Pie chart + dossier breakdown */}
            <div className="grid lg:grid-cols-12 gap-10 bg-[#0b1220] border border-white/[0.06] p-6 lg:p-10">
                <div className="lg:col-span-5">
                    <div className="overline text-[#d4af37]/70 mb-4">
                        Répartition globale
                    </div>
                    {total === 0 ? (
                        <p className="text-[12px] text-[#f4ecd8]/40 italic font-light py-12">
                            Aucune donnée financière saisie pour l'instant.
                            Ajoutez une facture ou un CA cible sur un dossier.
                        </p>
                    ) : (
                        <>
                            <div className="h-[240px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={chartData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={55}
                                            outerRadius={95}
                                            paddingAngle={2}
                                            stroke="#0b1220"
                                            strokeWidth={2}
                                        >
                                            {chartData.map((entry, idx) => (
                                                <Cell
                                                    key={`c-${idx}`}
                                                    fill={entry.color}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{
                                                background: "#070d18",
                                                border: "1px solid rgba(212,175,55,0.3)",
                                                color: "#f4ecd8",
                                                fontSize: "12px",
                                            }}
                                            formatter={(v) => formatEUR(v)}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <ul className="mt-4 space-y-2">
                                {chartData.map((s) => (
                                    <li
                                        key={s.name}
                                        className="flex items-center justify-between text-[12px] font-serif text-[#f4ecd8]/80"
                                    >
                                        <span className="flex items-center gap-2">
                                            <span
                                                className="inline-block w-2.5 h-2.5"
                                                style={{ backgroundColor: s.color }}
                                            />
                                            {s.name}
                                        </span>
                                        <span className="tracking-wide">
                                            {formatEUR(s.value)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>

                <div className="lg:col-span-7">
                    <div className="overline text-[#d4af37]/70 mb-4">
                        Par dossier
                    </div>
                    {!finance.dossiers || finance.dossiers.length === 0 ? (
                        <p className="text-[12px] text-[#f4ecd8]/40 italic font-light py-6">
                            Aucun dossier avec des données financières.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 text-[10px] tracking-[0.24em] uppercase text-[#d4af37]/60 pb-2 border-b border-white/[0.08]">
                                <span>Dossier</span>
                                <span className="text-right w-[120px]">Encaissé</span>
                                <span className="text-right w-[120px]">En cours</span>
                                <span className="text-right w-[120px]">Cible</span>
                            </div>
                            {finance.dossiers.slice(0, 8).map((d) => (
                                <Link
                                    key={d.id}
                                    to={`/cockpit/dossiers/${d.id}`}
                                    data-testid={`finance-dossier-row-${d.id}`}
                                    className="grid grid-cols-[1fr_auto_auto_auto] gap-4 py-3 border-b border-white/[0.04] items-center hover:bg-white/[0.02] transition-colors duration-500 group"
                                >
                                    <span className="font-serif text-[14px] text-[#f4ecd8] truncate group-hover:text-[#d4af37] transition-colors">
                                        {d.name}
                                    </span>
                                    <span className="text-right text-[13px] text-[#d4af37] w-[120px] tabular-nums">
                                        {formatEUR(d.realized)}
                                    </span>
                                    <span className="text-right text-[13px] text-[#8fa3c4] w-[120px] tabular-nums">
                                        {formatEUR(d.pending)}
                                    </span>
                                    <span className="text-right text-[13px] text-[#f4ecd8]/65 w-[120px] tabular-nums">
                                        {formatEUR(d.target)}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Dual progression — Mission vs Finalisation */}
            <DualProgression finance={finance} />

            {/* XV Paliers — discreet milestone line */}
            <XVPaliers totalRealized={realized + pending} />
        </section>
    );
}

// ──────────────────────────────────────────────────────────────
// DualProgression — two slim progress lines: Mission vs Finalisation
// ──────────────────────────────────────────────────────────────

function ProgressTrack({ label, sublabel, realized, pending, target, color, testid }) {
    const total = (target || 0) > 0 ? target : Math.max(realized + pending, 1);
    const rPct = target > 0 ? Math.min(100, (realized / target) * 100) : 0;
    const pPct = target > 0 ? Math.min(100 - rPct, (pending / target) * 100) : 0;
    const overall = target > 0
        ? Math.min(100, ((realized + pending) / target) * 100)
        : 0;
    return (
        <div data-testid={testid}>
            <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/75">
                        {label}
                    </div>
                    <div className="text-[11px] text-[#f4ecd8]/40 italic font-light mt-1 max-w-md">
                        {sublabel}
                    </div>
                </div>
                <div className="text-[13px] font-serif text-[#f4ecd8]/85 tabular-nums">
                    <span style={{ color }}>{formatEUR(realized)}</span>
                    <span className="text-[#f4ecd8]/35">{"  /  "}</span>
                    <span className="text-[#f4ecd8]/65">
                        {target > 0 ? formatEUR(target) : "cible à définir"}
                    </span>
                    {target > 0 && (
                        <span className="text-[#f4ecd8]/35 ml-2 tabular-nums">
                            · {overall.toFixed(0)}%
                        </span>
                    )}
                </div>
            </div>
            <div className="relative h-px bg-white/[0.07]">
                <div
                    className="absolute inset-y-0 left-0 transition-[width] duration-1000"
                    style={{
                        width: `${rPct}%`,
                        background: color,
                        height: "1px",
                    }}
                />
                <div
                    className="absolute inset-y-0 transition-[width,left] duration-1000"
                    style={{
                        left: `${rPct}%`,
                        width: `${pPct}%`,
                        background: `${color}55`,
                        height: "1px",
                    }}
                />
            </div>
        </div>
    );
}

function DualProgression({ finance }) {
    const mission = finance?.mission || { realized: 0, pending: 0, target: 0 };
    const finalisation = finance?.finalisation || { realized: 0, pending: 0, target: 0 };
    const [editing, setEditing] = useState(false);
    const [missionDraft, setMissionDraft] = useState(String(mission.target || ""));
    const [finalDraft, setFinalDraft] = useState(String(finalisation.target || ""));
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await cockpit.put("/settings/targets", {
                target_mission: Number(missionDraft) || 0,
                target_finalisation: Number(finalDraft) || 0,
            });
            setEditing(false);
            // Soft reload to refresh dashboard
            window.location.reload();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="finance-dual-progression"
            className="mt-12 pt-10 border-t border-white/[0.05] space-y-10"
        >
            <ProgressTrack
                label="Honoraires de mission"
                sublabel="Audits · cadrage · structuration · accompagnement initial"
                realized={mission.realized}
                pending={mission.pending}
                target={mission.target}
                color="#d4af37"
                testid="progress-mission"
            />
            <ProgressTrack
                label="Accompagnement & finalisation"
                sublabel="Success fees · closings · accompagnement stratégique"
                realized={finalisation.realized}
                pending={finalisation.pending}
                target={finalisation.target}
                color="#a98a3a"
                testid="progress-finalisation"
            />
            <div className="flex items-center justify-end gap-3 pt-1">
                {editing ? (
                    <>
                        <input
                            data-testid="target-mission-input"
                            type="number"
                            placeholder="Cible mission (€)"
                            value={missionDraft}
                            onChange={(e) => setMissionDraft(e.target.value)}
                            className="w-[180px] bg-transparent border-b border-[#d4af37]/40 focus:border-[#d4af37] outline-none py-1.5 text-[12px] font-serif text-[#f4ecd8] tabular-nums text-right"
                        />
                        <input
                            data-testid="target-finalisation-input"
                            type="number"
                            placeholder="Cible finalisation (€)"
                            value={finalDraft}
                            onChange={(e) => setFinalDraft(e.target.value)}
                            className="w-[200px] bg-transparent border-b border-[#d4af37]/40 focus:border-[#d4af37] outline-none py-1.5 text-[12px] font-serif text-[#f4ecd8] tabular-nums text-right"
                        />
                        <button
                            type="button"
                            data-testid="targets-save"
                            disabled={busy}
                            onClick={save}
                            className="text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                        >
                            {busy ? "…" : "Valider"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditing(false)}
                            className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/40 hover:text-[#f4ecd8]/70"
                        >
                            Annuler
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        data-testid="targets-edit"
                        onClick={() => {
                            setMissionDraft(String(mission.target || ""));
                            setFinalDraft(String(finalisation.target || ""));
                            setEditing(true);
                        }}
                        className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45 hover:text-[#d4af37] transition-colors"
                    >
                        Ajuster les cibles annuelles
                    </button>
                )}
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────
// XVPaliers — psychological milestones (very discreet)
// ──────────────────────────────────────────────────────────────

const PALIERS = [25000, 100000, 250000, 500000, 750000];

function XVPaliers({ totalRealized }) {
    return (
        <div
            data-testid="xv-paliers"
            className="mt-10 pt-6 border-t border-white/[0.04] flex items-center justify-between flex-wrap gap-4"
        >
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/55">
                Paliers XV
            </div>
            <ul className="flex items-center gap-5 sm:gap-8 flex-wrap">
                {PALIERS.map((p, i) => {
                    const reached = totalRealized >= p;
                    return (
                        <li
                            key={p}
                            data-testid={`palier-${p}`}
                            className="flex items-center gap-3"
                        >
                            <span
                                className={`text-[12px] tracking-[0.18em] font-serif transition-all duration-700 ${
                                    reached
                                        ? "text-[#d4af37]"
                                        : "text-[#f4ecd8]/30"
                                }`}
                                style={
                                    reached
                                        ? {
                                              textShadow: "0 0 12px rgba(212,175,55,0.25)",
                                              fontStyle: "italic",
                                          }
                                        : {}
                                }
                            >
                                {p >= 1000 ? `${p / 1000}k` : p}
                            </span>
                            {i < PALIERS.length - 1 && (
                                <span className="text-[#f4ecd8]/15 text-[10px]">
                                    —
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}



// ──────────────────────────────────────────────────────────────
// FinanceTimeseries — Monthly / Yearly stacked bar chart
// ──────────────────────────────────────────────────────────────

const MONTH_LABELS_FR = [
    "Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin",
    "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc.",
];

function formatBucketLabel(key, period) {
    if (period === "monthly" && key.length === 7) {
        const [y, m] = key.split("-");
        const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1));
        return `${MONTH_LABELS_FR[idx]} ${y.slice(2)}`;
    }
    return key;
}

const compactEUR = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
    if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)} k€`;
    return `${Math.round(n)} €`;
};

function FinanceTimeseries({ finance, onTargetSaved }) {
    const [period, setPeriod] = useState("monthly");
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [editingTarget, setEditingTarget] = useState(false);
    const [targetDraft, setTargetDraft] = useState("");
    const [savingTarget, setSavingTarget] = useState(false);

    useEffect(() => {
        let cancel = false;
        setData(null);
        cockpit
            .get(`/dashboard/finance-timeseries?period=${period}`)
            .then(({ data }) => {
                if (cancel) return;
                setData(data);
                setErr("");
            })
            .catch((e) => {
                if (cancel) return;
                setErr(e?.response?.data?.detail || "Erreur de chargement");
            });
        return () => {
            cancel = true;
        };
    }, [period]);

    const monthlyTarget = Number(finance?.monthly_target) || 0;
    const monthRealized = Number(finance?.month_realized) || 0;
    const monthPending = Number(finance?.month_pending) || 0;
    const monthEngaged = monthRealized + monthPending;
    const monthProgress = monthlyTarget > 0
        ? Math.min(100, (monthRealized / monthlyTarget) * 100)
        : 0;

    const saveTarget = async () => {
        setSavingTarget(true);
        try {
            await cockpit.put("/settings/monthly-revenue-target", {
                monthly_revenue_target: Number(targetDraft) || 0,
            });
            setEditingTarget(false);
            if (onTargetSaved) onTargetSaved();
        } finally {
            setSavingTarget(false);
        }
    };

    const series = (data?.series || []).map((b) => ({
        ...b,
        label: formatBucketLabel(b.key, period),
    }));
    const totalRealized = series.reduce((s, b) => s + (b.realized || 0), 0);
    const totalPending = series.reduce((s, b) => s + (b.pending || 0), 0);
    const hasData = totalRealized + totalPending > 0;

    return (
        <section
            data-testid="cockpit-finance-timeseries"
            className="mb-14 lg:mb-16"
        >
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3">
                        <CalendarRange
                            strokeWidth={1}
                            size={14}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Évolution temporelle
                    </div>
                    <h2
                        className="font-serif text-[24px] sm:text-[28px] text-[#f4ecd8]"
                        style={{ fontWeight: 400 }}
                    >
                        Chiffre d'affaires {period === "monthly" ? "mensuel" : "annuel"}
                    </h2>
                    <p className="mt-2 text-[12px] text-[#f4ecd8]/45 italic font-light">
                        {period === "monthly"
                            ? "Sur les 12 derniers mois — encaissé vs en cours"
                            : "Sur les 5 dernières années — encaissé vs en cours"}
                    </p>
                </div>
                <div
                    className="inline-flex border border-white/15"
                    data-testid="finance-timeseries-toggle"
                >
                    {[
                        { v: "monthly", l: "Mensuel" },
                        { v: "yearly", l: "Annuel" },
                    ].map((opt) => (
                        <button
                            key={opt.v}
                            type="button"
                            data-testid={`finance-timeseries-${opt.v}`}
                            onClick={() => setPeriod(opt.v)}
                            className={`px-5 py-2 text-[10px] tracking-[0.28em] uppercase transition-all duration-500 ${
                                period === opt.v
                                    ? "bg-[#d4af37]/15 text-[#d4af37] border-r border-l border-[#d4af37]/40"
                                    : "text-[#f4ecd8]/55 hover:text-[#f4ecd8]"
                            }`}
                        >
                            {opt.l}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-[#0b1220] border border-white/[0.06] p-6 lg:p-8">
                {err ? (
                    <p className="text-[12px] text-[#e89090] py-8">{err}</p>
                ) : !data ? (
                    <p className="text-[12px] text-[#f4ecd8]/40 py-8 italic">
                        Chargement…
                    </p>
                ) : !hasData ? (
                    <p className="text-[12px] text-[#f4ecd8]/40 italic font-light py-10 text-center">
                        Aucune facture sur la période sélectionnée.
                    </p>
                ) : (
                    <>
                        <div className="h-[320px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={series}
                                    margin={{ top: 12, right: 16, left: 0, bottom: 8 }}
                                >
                                    <CartesianGrid
                                        stroke="rgba(244,236,216,0.05)"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fill: "#f4ecd8aa", fontSize: 11 }}
                                        axisLine={{ stroke: "rgba(244,236,216,0.1)" }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fill: "#f4ecd8aa", fontSize: 11 }}
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={compactEUR}
                                    />
                                    <Tooltip
                                        cursor={{ fill: "rgba(212,175,55,0.06)" }}
                                        contentStyle={{
                                            background: "#070d18",
                                            border: "1px solid rgba(212,175,55,0.3)",
                                            color: "#f4ecd8",
                                            fontSize: "12px",
                                        }}
                                        formatter={(v, name) => [
                                            formatEUR(v),
                                            name === "realized" ? "Encaissé" : "En cours",
                                        ]}
                                        labelStyle={{ color: "#d4af37" }}
                                    />
                                    <Legend
                                        wrapperStyle={{
                                            fontSize: "11px",
                                            color: "#f4ecd8",
                                            paddingTop: "12px",
                                        }}
                                        formatter={(v) =>
                                            v === "realized" ? "Encaissé" : "En cours"
                                        }
                                    />
                                    <Bar
                                        dataKey="realized"
                                        stackId="ca"
                                        fill="#d4af37"
                                        radius={[0, 0, 0, 0]}
                                    />
                                    <Bar
                                        dataKey="pending"
                                        stackId="ca"
                                        fill="#8fa3c4"
                                        radius={[3, 3, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-x-10 gap-y-2 text-[12px] text-[#f4ecd8]/70 font-serif">
                            <span className="flex items-center gap-2">
                                <span className="inline-block w-2.5 h-2.5 bg-[#d4af37]" />
                                Total encaissé sur la période :
                                <span className="text-[#d4af37] tabular-nums">
                                    {formatEUR(totalRealized)}
                                </span>
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="inline-block w-2.5 h-2.5 bg-[#8fa3c4]" />
                                Total en cours :
                                <span className="text-[#8fa3c4] tabular-nums">
                                    {formatEUR(totalPending)}
                                </span>
                            </span>
                        </div>
                    </>
                )}
            </div>

            {/* Discreet monthly target line — only when monthly */}
            {period === "monthly" && (
                <div
                    data-testid="finance-monthly-target"
                    className="mt-6 pt-6 border-t border-white/[0.05] flex items-start justify-between flex-wrap gap-4"
                >
                    <div className="text-[12.5px] font-serif text-[#f4ecd8]/70 leading-relaxed">
                        <span className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/65 block mb-1.5">
                            Objectif du mois
                        </span>
                        {monthlyTarget > 0 ? (
                            <>
                                <span className="text-[#d4af37] tabular-nums">
                                    {formatEUR(monthRealized)}
                                </span>{" "}
                                encaissés
                                {monthPending > 0 && (
                                    <>
                                        <span className="text-[#f4ecd8]/35">{" · "}</span>
                                        <span className="text-[#8fa3c4] tabular-nums">
                                            {formatEUR(monthPending)}
                                        </span>{" "}
                                        en cours
                                    </>
                                )}
                                <span className="text-[#f4ecd8]/35">{"  /  "}</span>
                                <span className="tabular-nums">
                                    {formatEUR(monthlyTarget)}
                                </span>{" "}
                                cible
                                <span className="text-[#f4ecd8]/35 ml-2 tabular-nums">
                                    · {monthProgress.toFixed(0)}%
                                </span>
                            </>
                        ) : (
                            <span className="text-[#f4ecd8]/40 italic font-light">
                                Aucune cible mensuelle définie.
                            </span>
                        )}
                    </div>
                    {editingTarget ? (
                        <div className="flex items-center gap-3">
                            <input
                                data-testid="monthly-target-input"
                                type="number"
                                step="100"
                                min="0"
                                value={targetDraft}
                                onChange={(e) => setTargetDraft(e.target.value)}
                                placeholder="50000"
                                className="w-[140px] bg-transparent border-b border-[#d4af37]/40 focus:border-[#d4af37] outline-none py-1.5 text-[13px] font-serif text-[#f4ecd8] tabular-nums text-right"
                                autoFocus
                            />
                            <button
                                type="button"
                                data-testid="monthly-target-save"
                                onClick={saveTarget}
                                disabled={savingTarget}
                                className="text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                            >
                                {savingTarget ? "…" : "Valider"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditingTarget(false)}
                                className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/40 hover:text-[#f4ecd8]/70 transition-colors"
                            >
                                Annuler
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            data-testid="monthly-target-edit"
                            onClick={() => {
                                setTargetDraft(monthlyTarget ? String(monthlyTarget) : "");
                                setEditingTarget(true);
                            }}
                            className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45 hover:text-[#d4af37] transition-colors"
                        >
                            {monthlyTarget > 0 ? "Ajuster la cible" : "Définir une cible"}
                        </button>
                    )}
                </div>
            )}
        </section>
    );
}

// ──────────────────────────────────────────────────────────────
// PrioritiesPanel — minimalist strategic to-dos.
// ──────────────────────────────────────────────────────────────

function PrioritiesPanel({ initial }) {
    const [items, setItems] = useState(initial || []);
    const [adding, setAdding] = useState(false);
    const [draftLabel, setDraftLabel] = useState("");
    const [draftDate, setDraftDate] = useState("");
    const [showArchive, setShowArchive] = useState(false);
    const [archive, setArchive] = useState(null);

    const reload = async () => {
        const { data } = await cockpit.get("/priorities");
        setItems(data);
    };

    const loadArchive = async () => {
        const { data } = await cockpit.get("/priorities/archive");
        setArchive(data);
    };

    useEffect(() => {
        if (showArchive && archive === null) loadArchive();
    }, [showArchive, archive]);

    const submit = async (e) => {
        e.preventDefault();
        if (!draftLabel.trim()) return;
        await cockpit.post("/priorities", {
            label: draftLabel.trim(),
            due_date: draftDate || "",
        });
        setDraftLabel("");
        setDraftDate("");
        setAdding(false);
        await reload();
    };

    const toggle = async (it) => {
        await cockpit.put(`/priorities/${it.id}`, { done: !it.done });
        await reload();
        if (showArchive) await loadArchive();
    };

    const remove = async (id) => {
        await cockpit.delete(`/priorities/${id}`);
        await reload();
    };

    const formatDate = (d) => {
        if (!d) return null;
        try {
            const dt = new Date(d);
            return dt.toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "short",
            });
        } catch {
            return d;
        }
    };

    const overdue = (d) => {
        if (!d) return false;
        try {
            return new Date(d) < new Date(new Date().toISOString().slice(0, 10));
        } catch {
            return false;
        }
    };

    const doneCount = items.filter((i) => i.done).length;

    return (
        <section
            data-testid="cockpit-priorities"
            className="mb-16 lg:mb-20"
        >
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3">
                        Priorités stratégiques
                    </div>
                    <h2
                        className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8]"
                        style={{ fontWeight: 400 }}
                    >
                        Agenda de la semaine
                    </h2>
                    {doneCount > 0 && (
                        <p className="mt-2 text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/55">
                            {doneCount} action{doneCount > 1 ? "s" : ""} effectuée{doneCount > 1 ? "s" : ""} · archivage automatique lundi prochain
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-5">
                    <button
                        type="button"
                        data-testid="priority-archive-toggle"
                        onClick={() => setShowArchive((v) => !v)}
                        className="text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/55 hover:text-[#d4af37] transition-colors duration-500"
                    >
                        {showArchive ? "Masquer l'archive" : "Archive"}
                    </button>
                    <button
                        type="button"
                        data-testid="priority-add-toggle"
                        onClick={() => setAdding((v) => !v)}
                        className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 hover:text-[#d4af37] transition-colors duration-500"
                    >
                        {adding ? "Annuler" : "+ Ajouter"}
                    </button>
                </div>
            </div>

            {adding && (
                <form
                    onSubmit={submit}
                    data-testid="priority-form"
                    className="mb-8 flex flex-col sm:flex-row gap-4 items-stretch sm:items-end pb-6 border-b border-white/[0.06]"
                >
                    <div className="flex-1">
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/65 mb-2">
                            Action
                        </label>
                        <input
                            data-testid="priority-label"
                            value={draftLabel}
                            onChange={(e) => setDraftLabel(e.target.value)}
                            placeholder="Relance Family Office Monaco…"
                            required
                            autoFocus
                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25"
                        />
                    </div>
                    <div className="sm:w-[160px]">
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/65 mb-2">
                            Échéance
                        </label>
                        <input
                            data-testid="priority-date"
                            type="date"
                            value={draftDate}
                            onChange={(e) => setDraftDate(e.target.value)}
                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[13px] font-serif text-[#f4ecd8]"
                        />
                    </div>
                    <button
                        type="submit"
                        data-testid="priority-submit"
                        className="px-5 py-2 text-[10px] tracking-[0.3em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 self-end"
                    >
                        Inscrire
                    </button>
                </form>
            )}

            {items.length === 0 ? (
                <p className="text-[12px] text-[#f4ecd8]/35 italic font-light py-6">
                    Aucune priorité en cours.
                </p>
            ) : (
                <ul>
                    {items.map((it) => (
                        <li
                            key={it.id}
                            data-testid={`priority-${it.id}`}
                            className="group grid grid-cols-[auto_1fr_auto_auto] gap-5 items-center py-4 border-b border-white/[0.04]"
                        >
                            <button
                                type="button"
                                onClick={() => toggle(it)}
                                data-testid={`priority-toggle-${it.id}`}
                                className={`w-[18px] h-[18px] border ${
                                    it.done
                                        ? "border-[#d4af37] bg-[#d4af37]/15"
                                        : "border-white/25 hover:border-[#d4af37]"
                                } transition-colors duration-300 flex items-center justify-center`}
                            >
                                {it.done && (
                                    <span className="text-[10px] text-[#d4af37] leading-none">
                                        ✓
                                    </span>
                                )}
                            </button>
                            <span
                                className={`font-serif text-[15px] tracking-wide ${
                                    it.done
                                        ? "text-[#f4ecd8]/40 line-through decoration-[#d4af37]/50"
                                        : "text-[#f4ecd8]/90"
                                }`}
                            >
                                {it.label}
                            </span>
                            <span
                                className={`text-[11px] tracking-wider tabular-nums ${
                                    it.done
                                        ? "text-[#f4ecd8]/30"
                                        : overdue(it.due_date)
                                          ? "text-[#e89090]/85"
                                          : "text-[#d4af37]/70"
                                }`}
                            >
                                {formatDate(it.due_date) || "—"}
                            </span>
                            <button
                                type="button"
                                onClick={() => remove(it.id)}
                                data-testid={`priority-delete-${it.id}`}
                                className="opacity-0 group-hover:opacity-100 text-[#f4ecd8]/30 hover:text-[#e89090] transition-all"
                                aria-label="Retirer la priorité"
                            >
                                ✕
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {showArchive && (
                <PrioritiesArchive
                    archive={archive}
                    onToggle={toggle}
                />
            )}
        </section>
    );
}

// ──────────────────────────────────────────────────────────────
// PrioritiesArchive — past completed tasks, grouped Year > Month > Day
// ──────────────────────────────────────────────────────────────
function PrioritiesArchive({ archive, onToggle }) {
    if (!archive) {
        return (
            <p
                data-testid="priorities-archive-loading"
                className="text-[12px] text-[#f4ecd8]/35 italic font-light py-6 mt-8 border-t border-white/[0.06] pt-6"
            >
                Chargement de l'archive…
            </p>
        );
    }
    if ((archive.items || []).length === 0) {
        return (
            <p
                data-testid="priorities-archive-empty"
                className="text-[12px] text-[#f4ecd8]/35 italic font-light py-6 mt-8 border-t border-white/[0.06] pt-6"
            >
                Archive vide — aucune priorité archivée à ce jour.
            </p>
        );
    }

    // Group by year > month > day using done_at (fallback created_at)
    const MONTHS_FR = [
        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
    ];
    const tree = {};
    for (const it of archive.items) {
        const iso = it.done_at || it.created_at || "";
        if (!iso) continue;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) continue;
        const y = d.getFullYear();
        const m = d.getMonth();
        const day = d.getDate();
        tree[y] = tree[y] || {};
        tree[y][m] = tree[y][m] || {};
        tree[y][m][day] = tree[y][m][day] || [];
        tree[y][m][day].push(it);
    }
    const years = Object.keys(tree)
        .map(Number)
        .sort((a, b) => b - a);

    return (
        <div
            data-testid="priorities-archive"
            className="mt-10 border-t border-white/[0.06] pt-8 space-y-8"
        >
            <div className="flex items-baseline justify-between gap-4">
                <h3
                    className="font-serif text-[18px] text-[#f4ecd8]"
                    style={{ fontWeight: 400 }}
                >
                    Archive
                </h3>
                <span className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/65">
                    {archive.count} action{archive.count > 1 ? "s" : ""} archivée{archive.count > 1 ? "s" : ""}
                </span>
            </div>
            {years.map((y) => {
                const months = Object.keys(tree[y])
                    .map(Number)
                    .sort((a, b) => b - a);
                return (
                    <div key={y} data-testid={`archive-year-${y}`}>
                        <div className="overline text-[#d4af37]/75 mb-4">{y}</div>
                        <div className="space-y-5 pl-4 border-l border-white/[0.06]">
                            {months.map((m) => {
                                const days = Object.keys(tree[y][m])
                                    .map(Number)
                                    .sort((a, b) => b - a);
                                return (
                                    <div
                                        key={m}
                                        data-testid={`archive-month-${y}-${m + 1}`}
                                    >
                                        <div className="text-[12.5px] tracking-wide text-[#f4ecd8]/70 mb-2">
                                            {MONTHS_FR[m]}
                                        </div>
                                        <div className="space-y-3">
                                            {days.map((day) => (
                                                <div
                                                    key={day}
                                                    data-testid={`archive-day-${y}-${m + 1}-${day}`}
                                                    className="pl-4 border-l border-white/[0.04]"
                                                >
                                                    <div className="text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/45 mb-2">
                                                        {String(day).padStart(2, "0")} {MONTHS_FR[m].toLowerCase()}
                                                    </div>
                                                    <ul className="space-y-1.5">
                                                        {tree[y][m][day].map((it) => (
                                                            <li
                                                                key={it.id}
                                                                data-testid={`archive-priority-${it.id}`}
                                                                className="group flex items-center gap-3"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    onClick={() => onToggle(it)}
                                                                    className="w-[14px] h-[14px] border border-[#d4af37]/60 bg-[#d4af37]/10 flex items-center justify-center hover:bg-[#d4af37]/25 transition-colors"
                                                                    aria-label="Rouvrir cette priorité"
                                                                >
                                                                    <span className="text-[8px] text-[#d4af37] leading-none">
                                                                        ✓
                                                                    </span>
                                                                </button>
                                                                <span className="font-serif text-[13.5px] text-[#f4ecd8]/60 line-through decoration-[#d4af37]/35">
                                                                    {it.label}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────
// CommercialPerformance — discreet 4 metric line
// ──────────────────────────────────────────────────────────────

function CommercialPerformance({ commercial }) {
    const cells = [
        { l: "Dossiers qualifiés", v: commercial.qualified },
        { l: "Audits engagés", v: commercial.audits_engaged },
        { l: "Missions ouvertes", v: commercial.missions_open },
        {
            l: "Transformation audit → mission",
            v: `${commercial.conversion_rate}%`,
            small: true,
        },
    ];
    return (
        <section
            data-testid="cockpit-commercial"
            className="mb-16 lg:mb-20"
        >
            <div className="overline text-[#d4af37]/70 mb-6">
                Performance commerciale
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14 pb-2">
                {cells.map((c) => (
                    <div key={c.l}>
                        <div
                            className={`font-serif ${c.small ? "text-[28px] sm:text-[34px]" : "text-[34px] sm:text-[42px]"} leading-none text-[#f4ecd8] mb-3 tabular-nums`}
                            style={{ fontWeight: 300 }}
                        >
                            {c.v}
                        </div>
                        <p className="text-[10.5px] tracking-[0.24em] uppercase text-[#f4ecd8]/45">
                            {c.l}
                        </p>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ──────────────────────────────────────────────────────────────
// PipelineSourcesMaturity — 3 discreet horizontal rows
// ──────────────────────────────────────────────────────────────

const MATURITY_LABELS = {
    faible: "Faible",
    "sérieux": "Sérieux",
    "avancé": "Avancé",
    prioritaire: "Prioritaire",
};

function PipelineSourcesMaturity({ pipeline, sources, maturity }) {
    const totalPipeline = pipeline.reduce((s, p) => s + p.count, 0);
    const totalSources = sources.reduce((s, p) => s + p.count, 0);

    return (
        <section
            data-testid="cockpit-pipeline-sources"
            className="mb-16 lg:mb-20 grid lg:grid-cols-12 gap-14 pt-10 border-t border-white/[0.05]"
        >
            {/* Pipeline */}
            <div className="lg:col-span-7">
                <div className="overline text-[#d4af37]/70 mb-6">
                    Pipeline commercial
                </div>
                {totalPipeline === 0 ? (
                    <p className="text-[12px] text-[#f4ecd8]/35 italic font-light">
                        Renseignez la phase pipeline d'un dossier pour voir la
                        progression apparaître ici.
                    </p>
                ) : (
                    <ul className="space-y-3">
                        {pipeline.map((p) => {
                            const pct = totalPipeline > 0
                                ? (p.count / totalPipeline) * 100
                                : 0;
                            return (
                                <li
                                    key={p.stage}
                                    data-testid={`pipeline-${p.stage}`}
                                    className="grid grid-cols-[160px_1fr_auto] items-center gap-5 py-1.5"
                                >
                                    <span className="text-[12px] tracking-[0.18em] uppercase text-[#f4ecd8]/70">
                                        {p.label}
                                    </span>
                                    <div className="relative h-px bg-white/[0.06]">
                                        <div
                                            className="absolute inset-y-0 left-0"
                                            style={{
                                                width: `${pct}%`,
                                                backgroundColor: "#d4af37",
                                                height: "1px",
                                            }}
                                        />
                                    </div>
                                    <span
                                        className={`text-[12px] tabular-nums tracking-wider ${
                                            p.count > 0
                                                ? "text-[#d4af37]"
                                                : "text-[#f4ecd8]/30"
                                        }`}
                                    >
                                        {p.count}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Sources + Maturity (stacked) */}
            <div className="lg:col-span-5 space-y-12">
                <div data-testid="cockpit-sources">
                    <div className="overline text-[#d4af37]/70 mb-6">
                        Sources d'acquisition
                    </div>
                    {totalSources === 0 ? (
                        <p className="text-[12px] text-[#f4ecd8]/35 italic font-light">
                            Renseignez l'origine d'un dossier pour activer ce
                            tableau.
                        </p>
                    ) : (
                        <ul className="space-y-2.5">
                            {sources.slice(0, 6).map((s) => (
                                <li
                                    key={s.source}
                                    data-testid={`source-${s.source}`}
                                    className="flex items-center justify-between gap-4 py-1.5 border-b border-white/[0.04]"
                                >
                                    <span className="text-[13px] font-serif text-[#f4ecd8]/80">
                                        {s.source}
                                    </span>
                                    <span className="text-[11px] tracking-wider text-[#d4af37]/85 tabular-nums">
                                        {s.count}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div data-testid="cockpit-maturity">
                    <div className="overline text-[#d4af37]/70 mb-6">
                        Maturité du portefeuille
                    </div>
                    <ul className="space-y-2.5">
                        {maturity.map((m) => (
                            <li
                                key={m.level}
                                data-testid={`maturity-${m.level}`}
                                className="flex items-center justify-between gap-4 py-1.5 border-b border-white/[0.04]"
                            >
                                <span className="text-[13px] font-serif text-[#f4ecd8]/80">
                                    {MATURITY_LABELS[m.level] || m.level}
                                </span>
                                <span
                                    className={`text-[11px] tracking-wider tabular-nums ${
                                        m.count > 0
                                            ? "text-[#d4af37]/85"
                                            : "text-[#f4ecd8]/30"
                                    }`}
                                >
                                    {m.count}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}


// ──────────────────────────────────────────────────────────────
// LiveMomentumLine — discreet "Activité en cours" + "Dernière avancée"
// ──────────────────────────────────────────────────────────────

const ACTIVITY_PHRASE = {
    dossier_created: (a) => `Nouveau dossier ouvert${a.extra?.name ? ` — ${a.extra.name}` : ""}`,
    dossier_updated: () => "Dossier mis à jour",
    dossier_deleted: () => "Dossier clôturé du cockpit",
    timeline_updated: () => "Avancement consigné dans la timeline",
    invoice_created: () => "Première facture émise",
    invoice_updated: (a) =>
        a.extra?.status === "paid" ? "Honoraire encaissé" : "Facture mise à jour",
    invoice_deleted: () => "Facture retirée du suivi",
    contact_created: () => "Interlocuteur ajouté à un dossier",
    contact_updated: () => "Fiche interlocuteur mise à jour",
    partner_created: () => "Partenaire ajouté au répertoire",
    partner_updated: () => "Fiche partenaire ajustée",
    doc_uploaded: () => "Document confidentiel déposé",
    client_access_resent: () => "Accès client réémis",
    report_published: () => "Synthèse confidentielle publiée",
    redeem: () => "Activation d'un accès client",
};

function LiveMomentumLine({ commercial, recent_activity, recent_dossiers }) {
    const active = commercial?.active_dossiers ?? 0;
    const advanced = commercial?.advanced_discussions ?? 0;
    const closings = commercial?.closings_in_preparation ?? 0;

    // Latest meaningful activity (skip pure logins)
    const latest = (recent_activity || []).find(
        (a) => a.action && a.action !== "login",
    );
    let lastPhrase = null;
    let lastAge = null;
    if (latest) {
        const builder = ACTIVITY_PHRASE[latest.action];
        lastPhrase = builder
            ? builder(latest)
            : latest.action.replace(/_/g, " ");
        lastAge = ageLabel(latest.at);
    } else if (recent_dossiers && recent_dossiers.length > 0) {
        const d = recent_dossiers[0];
        lastPhrase = `Dossier mis à jour — ${d.name}`;
        lastAge = ageLabel(d.updated_at);
    }

    return (
        <div
            data-testid="dashboard-momentum"
            className="mt-8 pt-6 border-t border-white/[0.05] flex flex-wrap items-center justify-between gap-x-10 gap-y-3"
        >
            <ul className="flex flex-wrap items-center gap-x-7 gap-y-2 text-[12.5px] font-serif text-[#f4ecd8]/65">
                <li data-testid="momentum-active" className="flex items-baseline gap-2">
                    <span className="text-[#d4af37] tabular-nums text-[15px]">
                        {active}
                    </span>
                    <span>{active > 1 ? "dossiers actifs" : "dossier actif"}</span>
                </li>
                <li className="text-[#f4ecd8]/20">·</li>
                <li
                    data-testid="momentum-advanced"
                    className="flex items-baseline gap-2"
                >
                    <span
                        className={`tabular-nums text-[15px] ${
                            advanced > 0 ? "text-[#d4af37]" : "text-[#f4ecd8]/40"
                        }`}
                    >
                        {advanced}
                    </span>
                    <span>
                        {advanced > 1
                            ? "discussions avancées"
                            : "discussion avancée"}
                    </span>
                </li>
                <li className="text-[#f4ecd8]/20">·</li>
                <li
                    data-testid="momentum-closing"
                    className="flex items-baseline gap-2"
                >
                    <span
                        className={`tabular-nums text-[15px] ${
                            closings > 0 ? "text-[#d4af37]" : "text-[#f4ecd8]/40"
                        }`}
                    >
                        {closings}
                    </span>
                    <span>
                        {closings > 1
                            ? "closings en préparation"
                            : "closing en préparation"}
                    </span>
                </li>
            </ul>
            {lastPhrase && (
                <div
                    data-testid="momentum-last"
                    className="text-[12px] font-serif text-[#f4ecd8]/55 italic"
                >
                    <span className="not-italic text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/65 mr-3">
                        Dernière avancée
                    </span>
                    {lastPhrase}
                    {lastAge && (
                        <span className="text-[#f4ecd8]/35"> · {lastAge}</span>
                    )}
                </div>
            )}
        </div>
    );
}



// ─────────────────────────────────────────────────────────────
// FollowupAlertsBlock — clients with whom no exchange has happened
// in 15+ days. Heuristic: max(dossier.updated_at, last note,
// last contribution, last client login).
// ─────────────────────────────────────────────────────────────
function FollowupAlertsBlock() {
    const [data, setData] = useState(null);
    useEffect(() => {
        cockpit
            .get("/network/clients-needing-followup")
            .then(({ data }) => setData(data))
            .catch(() => setData({ items: [], threshold_days: 15 }));
    }, []);

    if (!data) return null;
    const items = data.items || [];
    if (items.length === 0) {
        return (
            <section
                data-testid="followup-alerts-empty"
                className="mb-12 lg:mb-14 border-l-2 border-[#d4af37]/30 pl-5 py-3"
            >
                <div className="overline text-[#d4af37]/70 mb-1">
                    <AlertCircle
                        strokeWidth={1}
                        size={12}
                        className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                    />
                    Suivi clients · au calme
                </div>
                <p className="text-[12px] text-[#f4ecd8]/45 italic font-light">
                    Aucun client silencieux depuis plus de {data.threshold_days} jours.
                </p>
            </section>
        );
    }
    return (
        <section
            data-testid="followup-alerts"
            className="mb-12 lg:mb-14 bg-[#070d18] border border-[#d4af37]/25 p-8 lg:p-10"
        >
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="overline text-[#d4af37]/85 mb-2">
                        <AlertCircle
                            strokeWidth={1}
                            size={12}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Alertes suivi clients
                    </div>
                    <h2
                        className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        {items.length} client{items.length > 1 ? "s" : ""} sans
                        échange depuis plus de {data.threshold_days} jours
                    </h2>
                </div>
                <p className="text-[11.5px] text-[#f4ecd8]/40 italic font-light max-w-md leading-relaxed">
                    Reprendre contact, fixer un point d'avancement ou envoyer
                    une note de lecture stratégique.
                </p>
            </div>
            <ul className="divide-y divide-white/[0.06]">
                {items.map((it) => (
                    <li
                        key={it.dossier_id}
                        data-testid={`followup-${it.dossier_id}`}
                        className="py-3"
                    >
                        <Link
                            to={`/cockpit/dossiers/${it.dossier_id}`}
                            className="grid grid-cols-[1fr_auto_auto] items-center gap-6 hover:bg-white/[0.02] px-2 py-1 transition-colors duration-500"
                        >
                            <div className="min-w-0">
                                <div
                                    className="font-serif text-[16px] text-[#f4ecd8] leading-tight truncate"
                                    style={{ fontWeight: 400 }}
                                >
                                    {it.dossier_name}
                                </div>
                                <div className="mt-0.5 text-[11px] text-[#f4ecd8]/45 truncate">
                                    {it.primary_contact || "Contact principal non défini"}
                                    {it.status && (
                                        <span className="text-[#d4af37]/55 ml-2">
                                            · {STATUS_LABELS[it.status] || it.status}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <span className="text-[10px] tracking-[0.26em] uppercase text-[#e89090]">
                                {it.days_silent} j de silence
                            </span>
                            <ArrowUpRight
                                strokeWidth={1.1}
                                size={14}
                                className="text-[#d4af37]/70"
                            />
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// SyntheseExtras — small inline block: CA cible cumulé + motivation
// ─────────────────────────────────────────────────────────────
function SyntheseExtras({ targetCumulative, motivation }) {
    if (!targetCumulative && !motivation) return null;
    const eur = new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    });
    return (
        <div className="mt-4 space-y-2">
            {targetCumulative > 0 && (
                <div
                    data-testid="synthese-target-cumulative"
                    className="text-[12.5px] text-[#f4ecd8]/55"
                >
                    <span className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/65 mr-2">
                        CA cible cumulé
                    </span>
                    <span className="font-serif text-[#f4ecd8]/85">
                        {eur.format(targetCumulative)}
                    </span>
                </div>
            )}
            {motivation && (
                <p
                    data-testid="synthese-motivation"
                    className="text-[14px] italic font-serif text-[#d4af37]/85 max-w-xl leading-relaxed"
                >
                    « {motivation} »
                </p>
            )}
        </div>
    );
}

